import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
  Chip,
  Card,
  CardContent,
  Divider,
  IconButton,
  Breadcrumbs,
  Link,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowBack, Add, Delete, PersonRemove } from '@mui/icons-material';
import { TeamVm, TeamRole } from 'api/management/team.vm';
import { getTeam } from 'api/management/get-team';
import { getTeamMembers } from 'api/management/get-team-members';
import { GetTeamMembersVm } from 'api/management/get-team-members.vm';
import { TeamMemberVm } from 'api/management/team-member.vm';
import { addTeamMember } from 'api/management/add-team-member';
import { removeTeamMember } from 'api/management/remove-team-member';
import { deleteTeam } from 'api/management/delete-team';
import { useSnackbar } from 'hooks/use-snackbar';
import AddTeamMemberDialog from 'pages/management/AddTeamMemberDialog';
import ConfirmActionDialog from 'components/ConfirmActionDialog';

const TeamPage = () => {
  const theme = useTheme();
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<TeamVm | null>(null);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<TeamMemberVm[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [memberToRemove, setMemberToRemove] = useState<TeamMemberVm | null>(null);
  const snackbar = useSnackbar();

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const fetchTeam = () => {
    if (!teamId) return;

    setLoading(true);
    getTeam(teamId, {
      success: (response: TeamVm) => {
        setTeam(response);
        setLoading(false);
        fetchTeamMembersWithWarehouse(response.warehouseId);
      },
      error: (err) => {
        console.error('Falha ao obter equipa:', err);
        snackbar.show('Falha ao carregar dados da equipa', null, 'error');
        setLoading(false);
      },
    });
  };

  const fetchTeamMembersWithWarehouse = (warehouseId: string) => {
    if (!teamId) return;

    setMembersLoading(true);
    getTeamMembers(teamId, warehouseId, '', {
      success: (response: GetTeamMembersVm) => {
        setMembers(response.objects);
        setMembersLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter membros da equipa:', err);
        snackbar.show('Falha ao carregar membros da equipa', null, 'error');
        setMembersLoading(false);
      },
    });
  };

  const fetchTeamMembers = () => {
    if (!teamId || !team?.warehouseId) return;
    fetchTeamMembersWithWarehouse(team.warehouseId);
  };

  const handleAddMember = (workerId: string) => {
    if (!teamId) return;

    addTeamMember(teamId, workerId, {
      success: () => {
        snackbar.show('Membro adicionado à equipa com sucesso!', null, 'success');
        fetchTeamMembers();
        setAddMemberDialogOpen(false);
      },
      error: (err) => {
        console.error('Falha ao adicionar membro:', err);
        snackbar.show('Falha ao adicionar membro à equipa', null, 'error');
      },
    });
  };

  const handleRemoveMember = (member: TeamMemberVm) => {
    setMemberToRemove(member);
    setConfirmTitle('Remover Membro');
    setConfirmMessage(`Tem certeza que deseja remover ${member.name} da equipa?`);
    setConfirmAction(() => () => confirmRemoveMember(member.memberId));
    setConfirmDialogOpen(true);
  };

  const confirmRemoveMember = (workerId: string) => {
    if (!teamId) return;

    removeTeamMember(teamId, workerId, {
      success: () => {
        snackbar.show('Membro removido da equipa com sucesso!', null, 'success');
        fetchTeamMembers();
        setConfirmDialogOpen(false);
        setMemberToRemove(null);
      },
      error: (err) => {
        console.error('Falha ao remover membro:', err);
        snackbar.show('Falha ao remover membro da equipa', null, 'error');
      },
    });
  };

  const handleDeleteTeam = () => {
    setConfirmTitle('Eliminar Equipa');
    setConfirmMessage(`Tem certeza que deseja eliminar a equipa "${team?.name}"? Esta ação não pode ser desfeita.`);
    setConfirmAction(() => () => confirmDeleteTeam());
    setConfirmDialogOpen(true);
  };

  const confirmDeleteTeam = () => {
    if (!teamId) return;

    deleteTeam(teamId, {
      success: () => {
        snackbar.show('Equipa eliminada com sucesso!', null, 'success');
        navigate('/management/teams');
      },
      error: (err) => {
        console.error('Falha ao eliminar equipa:', err);
        snackbar.show('Falha ao eliminar equipa', null, 'error');
      },
    });
  };

  useEffect(() => {
    fetchTeam();
  }, [teamId]);

  const getRoleLabel = (role: TeamRole) => {
    const roleConfig = {
      [TeamRole.INVENTORY]: 'Inventário',
      [TeamRole.RECEPTION]: 'Receção',
      [TeamRole.PICKING]: 'Expedição',
      [TeamRole.PRODUCTION]: 'Produção',
    };

    return roleConfig[role];
  };

  const getRoleColor = (role: TeamRole) => {
    const colorConfig = {
      [TeamRole.INVENTORY]: 'primary',
      [TeamRole.RECEPTION]: 'success',
      [TeamRole.PICKING]: 'warning',
      [TeamRole.PRODUCTION]: 'info',
    };

    return colorConfig[role] as 'primary' | 'success' | 'warning' | 'info';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!team) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Equipa não encontrada
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" onClick={handleBackNavigation} sx={{ cursor: 'pointer' }}>
            Equipas
          </Link>
          <Typography color="text.primary">{team.name}</Typography>
        </Breadcrumbs>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Informações da Equipa
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mr: 2, minWidth: 120 }}>
                Nome:
              </Typography>
              <Typography variant="body1">{team.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mr: 2, minWidth: 120 }}>
                Função:
              </Typography>
              <Chip label={getRoleLabel(team.role)} color={getRoleColor(team.role)} size="small" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mr: 2, minWidth: 120 }}>
                Armazém:
              </Typography>
              <Typography variant="body1">{team.warehouseId}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Membros da Equipa ({members.length})</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => setAddMemberDialogOpen(true)} size="small">
              Adicionar Membro
            </Button>
            <Button variant="outlined" color="error" startIcon={<Delete />} onClick={handleDeleteTeam} size="small">
              Eliminar Equipa
            </Button>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {membersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : members.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Esta equipa ainda não tem membros. Clique em "Adicionar Membro" para começar.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.memberId} hover>
                    <TableCell>{member.name}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton color="error" size="small" onClick={() => handleRemoveMember(member)} title="Remover membro">
                          <PersonRemove />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <AddTeamMemberDialog
        open={addMemberDialogOpen}
        onClose={() => setAddMemberDialogOpen(false)}
        onSubmit={handleAddMember}
        excludeWorkerIds={members.map((m) => m.memberId)}
      />

      <ConfirmActionDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
        }}
        title={confirmTitle}
        message={confirmMessage}
      />
    </Box>
  );
};

export default TeamPage;
